import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Text, tokens, Tooltip } from '@hotjar/hotsauce';
import { getToken } from '@hotjar/webapp/features/Playbacks/store/utils';
import { constructUserAttributes } from '@hotjar/webapp/features/Playbacks/utils/constructUserAttributes';
import type { Recording, UserAttribute } from '@hotjar/webapp/features/Playbacks/types';
import { sanitize } from '@hotjar/webapp/lib/sanitize';
import { DataTypeEnum } from '@hotjar/webapp/features/Playbacks/types';
import type { IconSetDefinitions } from '@hotjar/hotsauce/build/components/Icon/icons';

import { Container, Title, Row, IconSpaced, Unknown, TextOverflow } from './styles';

type Props = {
  recording: Recording;
};

const dataTypeToIcon: Record<string, keyof IconSetDefinitions> = {
  [DataTypeEnum.EMAIL]: 'email',
  [DataTypeEnum.BOOLEAN]: 'boolean',
  [DataTypeEnum.DATE]: 'date',
  [DataTypeEnum.STRING]: 'string',
  [DataTypeEnum.NUMBER]: 'number',
  [DataTypeEnum.UNKNOWN]: 'unknown',
};

const getUserAttributeTooltip = (userAttribute: UserAttribute): JSX.Element => {
  let attributeValue;
  if (userAttribute.value !== null) {
    attributeValue = sanitize(userAttribute.value);
  } else {
    attributeValue = 'Unknown';
  }
  return (
    <>
      {sanitize(userAttribute.name)}:
      <br /> {attributeValue}
    </>
  );
};

export const UserAttributes = ({ recording }: Props): JSX.Element | null => {
  const { t } = useTranslation();

  const sharingToken = getToken() || '';
  const effectivePlan = recording.effective_plan;

  const isSharedRecording = Boolean(sharingToken);
  const canViewUserAttributes =
    !isSharedRecording && (effectivePlan === 'business' || effectivePlan === 'scale');
  if (!canViewUserAttributes) {
    return null;
  }
  const userAttributes = constructUserAttributes(recording.user_attributes, recording.user_id);
  if (userAttributes.length === 0) {
    return null;
  }

  return (
    <Container>
      <Title>{t('common:player.side-panel.user-attributes.title')}</Title>
      {userAttributes.map((attribute) => (
        <Tooltip
          key={attribute.name + attribute.value}
          placement="left"
          text={getUserAttributeTooltip(attribute) as unknown as string}
        >
          <Row data-hj-suppress>
            <span
              css={css`
                display: flex;
                align-items: center;

                ${tokens.truncateText()};
              `}
            >
              <IconSpaced
                color="text_light"
                icon={
                  attribute.name === 'user_id' ? 'user_single' : dataTypeToIcon[attribute.datatype]
                }
                description="user attribute"
              />
              <Text
                textType="caption"
                css={css`
                  max-width: 120px;
                  ${tokens.truncateText()};
                `}
              >
                {attribute.name}
              </Text>
              <span>:&nbsp;</span>
              {attribute.value !== null && (
                <TextOverflow textType="subtitle3">{attribute.value}</TextOverflow>
              )}
              {attribute.value === null && <Unknown>Unknown&nbsp;</Unknown>}
            </span>
          </Row>
        </Tooltip>
      ))}
    </Container>
  );
};
